import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Sprint 24.12 — the in-app analogue of the MCP `notifications/tools/list_changed`
 * channel (Sprint 14f).
 *
 * External hosts (Claude Desktop, Cursor) re-fetch tools/list when the server
 * emits that notification. The in-app console can't — its transport is the
 * `/ai/chat` stream, not `/mcp` — so instead it invalidates the tool-catalogue-
 * shaped React Query caches whenever the available-tool surface changes.
 *
 * Tool-surface changes driven by a CHAT TOOL CALL (equip/unequip, entity-type
 * CRUD, wrapped-API CRUD, …) are already handled: ToolCallCard fires
 * `invalidateForTool` from the mutation-registry on every completed tool result.
 * The one trigger that is NOT a chat tool call — and therefore invisible to that
 * path — is an OAuth connector authorize completing in a popup. So
 * `OAuthPromptCard` broadcasts a window-level `kuvio:tools-changed` event on
 * completion, and this hook listens for it and refreshes the catalogue caches.
 *
 * (The server-side `data-tools_changed` stream part the spec sketched is omitted:
 * for in-app tool calls it would duplicate the mutation-registry invalidation,
 * and the chat's own tool surface is already rebuilt per request — see the spec's
 * Decision 5. The window event covers the genuinely-uncovered OAuth case.)
 */

export interface ToolsChangedDetail {
  source?: string;
  reason?: string;
  connectionId?: string;
}

/** Catalogue / equipment list queries — broad, cheap to invalidate. */
const TOOL_CATALOGUE_KEYS: readonly (readonly string[])[] = [
  ["mcp-endpoints"],
  ["connections-unified"],
  ["connections"],
  ["agent-equipment"],
  ["agents-equipped-with"],
  ["entity-types"],
  ["wrapped-apis"],
  ["library-tree"],
];

export const TOOLS_CHANGED_EVENT = "kuvio:tools-changed";

export function useToolsChanged(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ToolsChangedDetail>).detail;
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[24.12] tools-changed", { source: detail?.source, reason: detail?.reason });
      }
      for (const key of TOOL_CATALOGUE_KEYS) {
        qc.invalidateQueries({ queryKey: [...key] });
      }
    };
    window.addEventListener(TOOLS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TOOLS_CHANGED_EVENT, handler);
  }, [qc]);
}
